/**
 * Label wrapping and overlap detection utilities for V2 bubble/scatter charts.
 * Ported from V1 charts to match the visual behavior of chartjs-plugin-datalabels.
 */

/**
 * Recursively wraps a label at word boundaries based on a character threshold.
 * Inserts '\n' at the last space within the threshold, or the first space after if none exists.
 * If no space exists at all, returns the full label unchanged.
 *
 * @param threshold - Maximum characters per line before wrapping
 * @param label - The label text to wrap
 * @returns The label with '\n' inserted at word boundaries
 */
export const processLabel = (threshold: number, label?: string): string => {
  if (!label) return ''
  if (label.length > threshold) {
    const index =
      label.slice(0, threshold).lastIndexOf(' ') === -1
        ? label.indexOf(' ')
        : label.slice(0, threshold).lastIndexOf(' ')

    if (index < 0) return label
    return (
      label.slice(0, index) +
      '\n' +
      processLabel(threshold, label.slice(index + 1))
    )
  } else {
    return label
  }
}

/**
 * Returns a character threshold for label wrapping based on font size.
 * Smaller fonts allow more characters per line; larger fonts need fewer.
 * Mirrors V1's getThreshold() logic but parameterized for Recharts.
 *
 * @param fontSizePx - Font size in pixels
 * @returns Character count threshold
 */
export const getLabelThreshold = (fontSizePx: number): number => {
  switch (fontSizePx) {
    case 18:
      return 12
    case 16:
      return 13
    case 12:
      return 16
    case 10:
      return 17
    default:
      return 15
  }
}

/**
 * Bounding box for overlap detection.
 */
export interface LabelBox {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Tests whether two bounding boxes overlap (axis-aligned rectangle intersection).
 *
 * @param a - First bounding box
 * @param b - Second bounding box
 * @returns true if the boxes overlap, false otherwise
 */
export const boxesOverlap = (a: LabelBox, b: LabelBox): boolean => {
  return !(
    a.x + a.width < b.x ||
    b.x + b.width < a.x ||
    a.y + a.height < b.y ||
    b.y + b.height < a.y
  )
}

/**
 * Estimates the bounding box for a multi-line label.
 * Conservative approximation (character width ~0.6 × fontSize, line height ~1.2 × fontSize).
 *
 * @param lines - Array of text lines
 * @param cx - Center X position
 * @param cy - Center Y position
 * @param radiusOrOffset - Bubble radius or fixed offset (label is positioned above the point)
 * @param fontSizePx - Font size in pixels
 * @param gap - Vertical gap between the point and the label (default 8)
 * @returns Bounding box for overlap testing
 */
export const estimateLabelBox = (
  lines: string[],
  cx: number,
  cy: number,
  radiusOrOffset: number,
  fontSizePx: number,
  gap = 8,
): LabelBox => {
  const maxLineLength = Math.max(...lines.map((line) => line.length))
  const width = maxLineLength * fontSizePx * 0.6
  const height = lines.length * fontSizePx * 1.2

  // Label is centered horizontally at cx, positioned above the bubble
  const x = cx - width / 2
  const y = cy - radiusOrOffset - gap - height

  return { x, y, width, height }
}

/**
 * Pre-compute which data points should show labels using greedy overlap avoidance.
 * This function must be called before rendering to avoid mutation during render.
 *
 * The algorithm:
 * 1. For each data point, wrap its label and estimate the bounding box
 * 2. Check if the box overlaps any already-claimed boxes
 * 3. If no overlap, add to visible set and claim the box
 * 4. If overlap, skip this label
 *
 * @param data - Array of data points with name field
 * @param fontSizePx - Font size in pixels
 * @param getPixelX - Function to convert data point to pixel X coordinate
 * @param getPixelY - Function to convert data point to pixel Y coordinate
 * @param getRadius - Function to get the radius (or offset) for the data point
 * @param gap - Vertical gap between point and label (default 8)
 * @returns Set of names that should display labels
 */
export const computeVisibleLabels = <T extends { name: string }>(
  data: T[],
  fontSizePx: number,
  getPixelX: (d: T) => number,
  getPixelY: (d: T) => number,
  getRadius: (d: T) => number,
  gap = 8,
): Set<string> => {
  const visibleNames = new Set<string>()
  const claimedBoxes: LabelBox[] = []
  const threshold = getLabelThreshold(fontSizePx)

  for (const item of data) {
    if (!item.name) continue

    // Wrap the label and split into lines
    const wrappedText = processLabel(threshold, item.name)
    const lines = wrappedText.split('\n')

    // Estimate bounding box
    const cx = getPixelX(item)
    const cy = getPixelY(item)
    const radius = getRadius(item)
    const labelBox = estimateLabelBox(lines, cx, cy, radius, fontSizePx, gap)

    // Check for overlap
    const hasOverlap = claimedBoxes.some((claimed) =>
      boxesOverlap(labelBox, claimed),
    )

    if (!hasOverlap) {
      claimedBoxes.push(labelBox)
      visibleNames.add(item.name)
    }
  }

  return visibleNames
}
